import React, { ReactNode, useMemo, useState, createContext, useContext } from 'react';
import { useMediaQuery } from '@chakra-ui/react';
import MobileMenu from '../component/MobileMenu/MobileMenu';
import SideMenu from '../component/SideMenu/SideMenu';
import { SideMenuOptionInterface } from '../shared';
import { useSafeTranslation } from '../hooks/useSafeTranslation';
import { useMatch, useParams } from 'react-router-dom';
import { filterMenuByPermissions } from '../page/TenantAdminDashboard/util';
import useAuth from '../hooks/useAuth';
import { useUnreadThreadCountContext } from './UnreadMessageContext';

interface Props {
  children: ReactNode;
}

export type ToggleSideNavType = 'hide' | 'minimal';

interface SideNavContextProps {
  toggleShowNav: (value: boolean) => void;
  toggleHideMobileNav: (value: boolean) => void;
  toggleMinimalNav: (value: boolean) => void;
  toggleMode: ToggleSideNavType;
  setToggleMode: React.Dispatch<React.SetStateAction<ToggleSideNavType>>;
  setNavOptions: (newOptions: SideMenuOptionInterface[]) => void;
  showNav: boolean;
  isMinimal: boolean;
  setIsMinimal: React.Dispatch<React.SetStateAction<boolean>>;
  navOptions: SideMenuOptionInterface[];
}

const SideNavContext = createContext<SideNavContextProps | undefined>(undefined);

export const useSideNavContext = () => {
  const context = useContext(SideNavContext);
  if (!context) {
    throw new Error('useSideNavContext must be used within a SideNavProvider');
  }
  return context;
};

export const SideNavProvider = ({ children }: Props) => {
  const { t, i18n } = useSafeTranslation();
  const { auth } = useAuth();
  const params = useParams();
  const match = useMatch('/tenantAdmin/id/:tenantId/*');
  const tenant = match?.params.tenantId || params.id;
  const [show, setShow] = useState<boolean>(true);
  const [hideMobileNav, setHideMobileNav] = useState<boolean>(false);
  const [isMinimal, setIsMinimal] = useState<boolean>(false);
  const [navOptions, setNavOptions] = useState<SideMenuOptionInterface[]>([]);
  const [toggleMode, setToggleMode] = useState<ToggleSideNavType>('hide');
  const [isNotFullSizeScreen] = useMediaQuery('(max-width: 1024px)');
  const isAdminAccount = auth.loggedUserDto.tenantAdmin || auth.loggedUserDto.superAdmin;
  const { unreadThreadCount } = useUnreadThreadCountContext();

  const mobileNavOptions: SideMenuOptionInterface[] = useMemo(
    () => [
      {
        header: t('child_owner_dashboard_element.meal_plan'),
        icon: 'cutlery',
        link: `${isAdminAccount ? `id/${tenant}/kitchen/food-menu/` : ''}meal-plan`,
        permission: 'MEAL_COOKING'
      },
      {
        header: t('child_owner_dashboard_element.absences'),
        icon: 'calendar',
        link: `${isAdminAccount ? `id/${tenant}/` : ''}absences`,
        permission: 'ORDERS'
      },
      {
        header: t('child_owner_dashboard_element.messages'),
        pointer: (
          <span className="absolute top-0.5 right-0.5 ml-1 mb-1.5 text-[10px] bg-brand-600 text-white rounded-full px-1.5 py-0.5">
            {unreadThreadCount}
          </span>
        ),
        icon: 'message',
        link: `${isAdminAccount ? `id/${tenant}/` : ''}mail-box/messages`,
        permission: 'MESSAGING'
      },
      {
        header: t('child_owner_dashboard_element.home_page'),
        icon: 'happyHomeIcon',
        link: `${isAdminAccount ? `id/${tenant}/dashboard` : 'main'}`,
        permission: 'ORDERS'
      }
    ],
    [i18n.language, unreadThreadCount]
  );

  const filteredMenu = useMemo(
    () => filterMenuByPermissions(mobileNavOptions, auth.loggedUserDto.availableModules),
    [mobileNavOptions, auth.loggedUserDto.availableModules]
  );

  const toggleShowNav = (value: boolean) => {
    setShow(value);
  };

  const toggleHideMobileNav = (value: boolean) => {
    setHideMobileNav(value);
  };

  const toggleMinimalNav = (value: boolean) => {
    setIsMinimal(value);
  };

  const updateNavOptions = (newOptions: SideMenuOptionInterface[]) => {
    setNavOptions(newOptions);
  };

  const contextValue = useMemo(
    () => ({
      toggleMode,
      setToggleMode,
      toggleShowNav,
      toggleMinimalNav,
      showNav: show,
      isMinimal,
      setIsMinimal,
      navOptions,
      setNavOptions: updateNavOptions,
      toggleHideMobileNav
    }),
    [show, isMinimal, setIsMinimal, navOptions, toggleMode, toggleHideMobileNav]
  );

  return (
    <SideNavContext.Provider value={contextValue}>
      <div className="flex h-screen w-screen overflow-hidden">
        {/* Sidebar */}
        <div
          className={`
        transition-all hidden lg:flex duration-300 ease-in-out border-r border-grayLight-200
        ${show ? (isMinimal ? 'w-20' : 'w-72') : 'w-0'}
        ${show ? 'translate-x-0' : '-translate-x-full'}
      `}
          style={{
            overflowY: 'auto',
            overflowX: 'hidden',
            transition: 'width 0.3s ease-in-out, transform 0.3s ease-in-out'
          }}>
          <SideMenu menuOptions={navOptions} />
        </div>

        {/* Main content */}
        <div className="flex-1 relative overflow-y-auto h-full">
          <div className="min-h-full">
            {children}
          </div>
        </div>

        {/* Mobile nav */}
        {!hideMobileNav && (
          <div className="lg:hidden fixed bottom-0 left-0 w-full h-[72px] z-50 bg-white border-t border-gray-200">
            <MobileMenu menuOptions={isAdminAccount ? filteredMenu : mobileNavOptions} />
          </div>
        )}
      </div>
    </SideNavContext.Provider>
  );
};
